/**
 * Token custody for the SPA.
 *
 *   - access token  — in memory only. Never written to storage: a 15-minute
 *     token that vanishes on tab close is an acceptable XSS blast radius.
 *   - refresh token — localStorage, so a reload doesn't force re-login
 *     (Security Architecture §5's 30-day rotating refresh). This is an
 *     XSS-exposed store; the mitigation is the API's own reuse-detection
 *     (a stolen-and-replayed refresh token revokes the whole family,
 *     Security Architecture §3.2) plus the short access-token TTL. A
 *     future httpOnly-cookie flow would need an API change (tokens are
 *     returned in the response body today) — noted for Phase 13/Security.
 *
 * Recorded as ADR-13A-002 in the Phase 13A documentation.
 */
const REFRESH_KEY = 'mw.rt';

let accessToken: string | null = null;
let accessExpiresAt: number | null = null;

/**
 * When a JWT expires, in ms since the epoch, from its `exp` claim; null when the token is not a decodable
 * JWT. The payload is not secret, and this is only used to refresh before the token dies, never to trust it.
 */
export function jwtExpiryMs(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(payload.length / 4) * 4, '='));
    const exp = (JSON.parse(json) as { exp?: unknown }).exp;
    return typeof exp === 'number' && Number.isFinite(exp) ? exp * 1000 : null;
  } catch {
    return null;
  }
}
const listeners = new Set<() => void>();

function readRefresh(): string | null {
  try {
    return window.localStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
}

function writeRefresh(token: string | null): void {
  try {
    if (token) window.localStorage.setItem(REFRESH_KEY, token);
    else window.localStorage.removeItem(REFRESH_KEY);
  } catch {
    /* private mode / disabled storage — session simply won't survive reload */
  }
}

export const tokenStore = {
  getAccessToken: (): string | null => accessToken,
  /** Null when unknown; callers then treat the token as usable and let a 401 correct them. */
  getAccessExpiresAt: (): number | null => accessExpiresAt,
  getRefreshToken: (): string | null => readRefresh(),
  hasSession: (): boolean => accessToken !== null || readRefresh() !== null,

  setTokens: (access: string, refresh: string): void => {
    accessToken = access;
    accessExpiresAt = jwtExpiryMs(access);
    writeRefresh(refresh);
    emit();
  },

  clear: (): void => {
    accessToken = null;
    accessExpiresAt = null;
    writeRefresh(null);
    emit();
  },

  /** Notified on any token change (login, refresh, logout) — the auth
   * context re-renders guarded routes off this. */
  subscribe: (fn: () => void): (() => void) => {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};

function emit(): void {
  for (const fn of listeners) fn();
}
